import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import OutlineButton from 'components/OutlineButton';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import InternalHeader from 'components/Navigation/InternalHeader';
import BankAccountModalContent from './ActionModalContent';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';
import Modal from 'components/Modal';
import Table from 'components/Table';

import { IBankAccount } from 'utils/interfaces';
import { BANK_ACCOUNTS } from 'utils/constants';
import { BUTTON_COLORS } from 'utils/enums';
import StorageService from 'utils/storage';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function BankAccountsPage() {
  const history = useNavigate();
  const auth = useAuth();

  const userLevel = StorageService.get('userData')?.level;
  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<Array<IBankAccount>>([]);

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionData, setActionData] = useState<IBankAccount | undefined>(undefined);
  const [deleteData, setDeleteData] = useState<IBankAccount | undefined>(undefined);

  const submitDelete = async () => {
    if (!deleteData) return;
    setModalLoading(true);
    let result: any = await generalApi.delete(`/bank_account/${deleteData._id}`);
    setModalLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('Cuenta bancaria eliminada');
    setRefresh(true);
    setDeleteData(undefined);
  };

  useEffect(() => {
    const getBankAccounts = async () => {
      setLoading(true);
      const result = await generalApi.get(
        userLevel === '30' ? '/bank_accounts' : '/company_bank_accounts'
      );
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setBankAccounts(result.data.bank_accounts);
      setLoading(false);
    };
    if (refresh) {
      getBankAccounts();
      setRefresh(false);
    }
  }, [generalApi, refresh, userLevel]);

  return (
    <PrivateRoute>
      <>
        <Modal
          isOpen={isActionOpen}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <BankAccountModalContent
            title={actionData ? 'Editar cuenta bancaria' : 'Crear cuenta bancaria'}
            editData={actionData}
            onClose={() => {
              setIsActionOpen(false);
              setActionData(undefined);
            }}
            onSuccess={() => {
              setRefresh(true);
              setIsActionOpen(false);
              setActionData(undefined);
            }}
          />
        </Modal>
        <Modal
          isOpen={deleteData !== undefined}
          onClose={() => {
            setDeleteData(undefined);
          }}
        >
          <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                Confirmar acción
              </h3>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <p>¿Está seguro que desea eliminar ésta cuenta bancaria?</p>
          </div>
          <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <SolidButton
              className="w-auto sm:ml-3"
              isLoading={modalLoading}
              onClick={() => submitDelete()}
            >
              Confirmar
            </SolidButton>
            <button
              type="button"
              onClick={() => setDeleteData(undefined)}
              className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancelar
            </button>
          </div>
        </Modal>
        <div className="min-h-full bg-gray-100 text-[#1B205A]">
          <InternalHeader />
          <div className="pb-8 pt-32 sm:pt-0 2xl:px-24">
            <div className="justify-between space-y-4 px-8 pt-8 pb-4 sm:flex sm:space-y-0">
              <p className="text-2xl font-bold">Mis cuentas bancarias</p>
              <div className="space-y-2 sm:ml-20 sm:flex sm:space-y-0 sm:space-x-4">
                {userLevel !== '32' && (
                  <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => setIsActionOpen(true)}>
                    Nueva cuenta bancaria
                  </OutlineButton>
                )}
                <OutlineButton
                  color={BUTTON_COLORS.BLUE}
                  onClick={() => history('/create_operation')}
                >
                  Nuevas Operaciones
                </OutlineButton>
              </div>
            </div>
            <Table
              isLoading={loading}
              loadingText="Buscando cuentas bancarias"
              initialSort={{
                key: 'created_at',
                order: 'desc',
              }}
              columns={[
                {
                  title: 'BANCO',
                  dataKey: 'name',
                },
                {
                  title: 'TIPO',
                  dataKey: 'account_type',
                  render: (item: IBankAccount) => BANK_ACCOUNTS[item.account_type],
                },
                {
                  title: 'MONEDA',
                  dataKey: 'currency',
                  defaultValue: '-',
                },
                {
                  title: 'NÚMERO',
                  dataKey: 'number',
                },
                {
                  title: 'CCI',
                  dataKey: 'cci',
                },
                {
                  title: 'DESCRIPCIÓN',
                  dataKey: 'description',
                },
                ...(userLevel === '30'
                  ? []
                  : [
                      {
                        title: 'OPCIONES',
                        dataKey: 'options',
                        disableSort: true,
                        render: (item: IBankAccount) => (
                          <div className="flex space-x-2 pr-2">
                            <SolidButton
                              color={BUTTON_COLORS.BLUE}
                              onClick={() => {
                                setIsActionOpen(true);
                                setActionData(item);
                              }}
                            >
                              Editar
                            </SolidButton>
                            <SolidButton
                              color={BUTTON_COLORS.RED}
                              onClick={() => setDeleteData(item)}
                            >
                              Eliminar
                            </SolidButton>
                          </div>
                        ),
                      },
                    ]),
              ]}
              data={bankAccounts}
            />
          </div>
        </div>
      </>
    </PrivateRoute>
  );
}
